/**
 * Contact page enhancements: lazy Google Maps facade and Shopify Forms shadow-DOM fixes.
 */
(() => {
  const CONTAINER = '.contact-page';

  const initMapFacade = () => {
    document.querySelectorAll('[data-contact-map]:not([data-contact-map-bound])').forEach((facade) => {
      /* Theme editor section renders must not add duplicate listeners. */
      facade.setAttribute('data-contact-map-bound', '');

      facade.addEventListener('click', (event) => {
        /* Preserve modified clicks on the fallback Google Maps link. */
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();

        const iframe = document.createElement('iframe');
        iframe.className = 'contact-map__frame';
        iframe.src = facade.dataset.mapSrc;
        iframe.title = facade.dataset.mapTitle || 'Map';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        facade.replaceWith(iframe);

        /* Replacing the focused link would otherwise move focus back to <body>. */
        iframe.focus({ preventScroll: true });
      });
    });
  };

  /**
   * Match stable CSS-module prefixes, not generated hashes. The phone country list already
   * opens downwards, so dropdown positioning only applies to the query-type list.
   */
  const DROPDOWN = 'div[class*="_dropdownContainer_"]:not(.phone-country-selector *)';
  const OPTIONS = `${DROPDOWN} div[class*="_optionList_"]`;
  const TOGGLE_TEXT = 'span[class*="_selectToggleText_"]:not(.phone-country-selector *)';

  /* Dropdown containers only exist while their list is open. */
  const ANY_DROPDOWN = 'div[class*="_dropdownContainer_"]';
  const CLIPPING_SECTIONS = [
    `section[class*="_formContainer_"]:has(${ANY_DROPDOWN})`,
    `section[class*="_inline_"]:has(${ANY_DROPDOWN})`,
  ].join(',');

  /* Room for all six query types (6 x 40px + 16px of padding = 256px), capped against the
     viewport so a short window gets a scrollable list instead of one running off the screen. */
  const LIST_MAX = 'min(19rem, 50vh)';

  const cssQuote = (value) => `"${String(value).replace(/[\\"]/g, '\\$&')}"`;

  /**
   * Fix field sizing, textarea usability, query-list direction and clipping. The placeholder
   * uses generated content so it cannot become a submitted value and affect Flow routing.
   */
  const buildPatch = (placeholder) =>
    [
      '.phone-country-selector > div{height:100%!important}',
      '.phone-country-selector button{height:100%!important;max-height:none!important}',
      'div:has(> textarea)::before{top:1px!important;left:1px!important;right:1px!important;width:auto!important}',
      'textarea{min-height:150px!important;resize:vertical!important}',
      `${CLIPPING_SECTIONS}{overflow:visible!important}`,
      `${DROPDOWN}{top:100%!important;bottom:auto!important;margin-top:4px!important;margin-bottom:0!important;height:auto!important;max-height:${LIST_MAX}!important;z-index:5!important;box-shadow:0 8px 20px rgb(0 0 0/0.16),0 0 0 1px rgb(var(--color-foreground-rgb,0 0 0)/0.55)!important}`,
      `${OPTIONS}{height:auto!important;max-height:${LIST_MAX}!important;overscroll-behavior:contain!important;scrollbar-width:thin!important;scrollbar-color:rgb(0 0 0/0.35) rgb(0 0 0/0.07)!important}`,
      /* Keep overflow discoverable in browsers with hidden overlay scrollbars. */
      `${OPTIONS}::-webkit-scrollbar{width:10px}`,
      `${OPTIONS}::-webkit-scrollbar-track{background:rgb(0 0 0/0.07)}`,
      `${OPTIONS}::-webkit-scrollbar-thumb{background:rgb(0 0 0/0.35);border:3px solid transparent;background-clip:content-box;border-radius:10px}`,
      placeholder &&
        `${TOGGLE_TEXT}:empty::after{content:${cssQuote(placeholder)};color:var(--form-placeholder-color)}`,
    ]
      .filter(Boolean)
      .join('');

  const STYLE_ID = 'hw-contact-forms-patch';

  /* The widget replaces its adopted stylesheets on re-render, so inject an idempotent style. */
  const applyPatch = (host, css) => {
    const root = host && host.shadowRoot;
    if (!root || root.getElementById(STYLE_ID)) return root;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    root.appendChild(style);
    return root;
  };

  /* Shared state prevents theme-editor section renders from leaking timers and observers. */
  let pollTimer = null;
  let observer = null;
  let observedRoot = null;

  const initForm = (container) => {
    const selector = `${CONTAINER} shopify-forms-embed`;
    const css = buildPatch(container.dataset.queryPlaceholder);

    clearInterval(pollTimer);

    const tick = () => {
      const root = applyPatch(document.querySelector(selector), css);
      if (!root || root === observedRoot) return;

      /* re-apply after any re-render of the widget */
      observedRoot = root;
      if (observer) observer.disconnect();
      observer = new MutationObserver(() => applyPatch(document.querySelector(selector), css));
      observer.observe(root, { childList: true, subtree: true });
    };

    /* Poll until the app creates/upgrades the host; keep polling to detect host replacement. */
    tick();
    const deadline = Date.now() + 30000;
    pollTimer = setInterval(() => {
      tick();
      if (Date.now() > deadline) clearInterval(pollTimer);
    }, 200);
  };

  const init = () => {
    const container = document.querySelector(CONTAINER);
    if (!container) return;
    initMapFacade();
    initForm(container);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor events fire for every section. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.(CONTAINER)) init();
  });
})();
